import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { sendAnalyticsEvent } from '../lib/analytics';

interface SystemGravity {
    mass: number;
    risk: number;
    velocity: number;
    caliber: number;
}

const DEFAULT_GRAVITY: SystemGravity = {
    mass: 0.5,
    risk: 0.1,
    velocity: 0.5,
    caliber: 0.0
};

const useMediaQuery = (query: string) => {
    const getMatches = () => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia(query).matches;
    };

    const [matches, setMatches] = useState(getMatches);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mql = window.matchMedia(query);
        const onChange = () => setMatches(mql.matches);
        onChange();

        if (mql.addEventListener) {
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        }

        mql.addListener(onChange);
        return () => mql.removeListener(onChange);
    }, [query]);

    return matches;
};

interface GravityDialsProps {
    compact?: boolean;
}

export const GravityDials: React.FC<GravityDialsProps> = ({ compact = false }) => {
    const [gravity, setGravity] = useState<SystemGravity>(DEFAULT_GRAVITY);
    const [loading, setLoading] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const isMobile = useMediaQuery('(max-width: 767px)');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const closeModal = () => setIsModalOpen(false);

    // Fetch initial
    useEffect(() => {
        fetch('/api/gravity')
            .then(res => res.ok ? res.json() : null)
            .then(data => {
                if (data) setGravity(data);
            })
            .catch(console.error);
    }, []);

    // Apply Visual Warp based on Mass
    useEffect(() => {
        document.documentElement.style.setProperty('--gravity-mass', gravity.mass.toString());
        if (gravity.mass > 0.8) {
            document.body.classList.add('gravity-warp-heavy');
        } else {
            document.body.classList.remove('gravity-warp-heavy');
        }
    }, [gravity.mass]);

    const handleChange = (key: keyof SystemGravity, value: number) => {
        const newGravity = { ...gravity, [key]: value };
        setGravity(newGravity);
        saveGravity(newGravity);

        // Emit analytics for interaction
        sendAnalyticsEvent('gravity_change', { key, value });
    };

    const saveGravity = (g: SystemGravity) => {
        setLoading(true);
        fetch('/api/gravity', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(g)
        })
            .then(res => res.json())
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        if (!isModalOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isModalOpen]);

    useEffect(() => {
        if (!isModalOpen) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [isModalOpen]);

    const dialControls = (
        <>
            {/* Mass */}
            <div title="Ambition / Weight. High Mass warps the UI and increases friction gravity.">
                <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-purple-400 font-bold">Mass (Ambition)</span>
                    <span className="font-mono">{gravity.mass.toFixed(2)}</span>
                </div>
                <input
                    type="range" min="0" max="1" step="0.1"
                    value={gravity.mass}
                    onChange={(e) => handleChange('mass', parseFloat(e.target.value))}
                    className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                />
            </div>

            {/* Risk */}
            <div title="Compliance / Safety. Low Risk (0.0) is strict. High Risk (1.0) allows chaotic thought density.">
                <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-red-400 font-bold">Risk (Compliance)</span>
                    <span className="font-mono">{gravity.risk.toFixed(2)}</span>
                </div>
                <input
                    type="range" min="0" max="1" step="0.1"
                    value={gravity.risk}
                    onChange={(e) => handleChange('risk', parseFloat(e.target.value))}
                    className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
                />
            </div>

            {/* Velocity */}
            <div title="Speed / Documentation. High Velocity demands faster output.">
                <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-blue-400 font-bold">Velocity (Speed)</span>
                    <span className="font-mono">{gravity.velocity.toFixed(2)}</span>
                </div>
                <input
                    type="range" min="0" max="1" step="0.1"
                    value={gravity.velocity}
                    onChange={(e) => handleChange('velocity', parseFloat(e.target.value))}
                    className="w-full h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
            </div>

            {/* Caliber */}
            <div className="pt-2 border-t border-gray-700/50 mt-2" title="Earned Gravity. Increases with completed Forge tasks.">
                <div className="flex justify-between text-[10px] text-gray-500">
                    <span>Caliber (Earned)</span>
                    <span className="font-mono text-yellow-500">{gravity.caliber.toFixed(2)}</span>
                </div>
            </div>
        </>
    );

    // Mobile: keep sidebar compact and open dials in a modal
    if (compact && isMobile) {
        return (
            <>
                <div
                    className="p-2 bg-black/40 border-t border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setIsModalOpen(true)}
                    title="Tap to open Strategy Dials"
                >
                    <div className="flex justify-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-purple-500" style={{ opacity: 0.3 + (gravity.mass * 0.7) }} title={`Mass: ${gravity.mass.toFixed(1)}`} />
                        <div className="w-2 h-2 rounded-full bg-red-500" style={{ opacity: 0.3 + (gravity.risk * 0.7) }} title={`Risk: ${gravity.risk.toFixed(1)}`} />
                        <div className="w-2 h-2 rounded-full bg-blue-500" style={{ opacity: 0.3 + (gravity.velocity * 0.7) }} title={`Velocity: ${gravity.velocity.toFixed(1)}`} />
                    </div>
                </div>

                {isModalOpen && (
                    <div className="fixed inset-0 z-50">
                        <div className="absolute inset-0 bg-black/70" onClick={closeModal} />
                        <div
                            className="absolute bottom-0 left-0 right-0 bg-gray-900 text-gray-200 border-t border-gray-700 rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto"
                            role="dialog"
                            aria-modal="true"
                        >
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700/70">
                                <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                                    Strategy Dials
                                    {loading && <span className="text-[10px] text-yellow-500 animate-pulse">Saving...</span>}
                                </div>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    aria-label="Close Strategy Dials"
                                    className="p-2 rounded-lg hover:bg-white/5 text-gray-300 hover:text-white transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="px-4 py-4 space-y-3">
                                {dialControls}
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }

    // Minimized View (Visual Indicators only)
    if (isCollapsed && compact) {
        return (
            <div
                className="p-2 bg-black/40 border-t border-white/5 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setIsCollapsed(false)}
                title="Click to expand Strategy Dials"
            >
                <div className="flex justify-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500" style={{ opacity: 0.3 + (gravity.mass * 0.7) }} title={`Mass: ${gravity.mass.toFixed(1)}`} />
                    <div className="w-2 h-2 rounded-full bg-red-500" style={{ opacity: 0.3 + (gravity.risk * 0.7) }} title={`Risk: ${gravity.risk.toFixed(1)}`} />
                    <div className="w-2 h-2 rounded-full bg-blue-500" style={{ opacity: 0.3 + (gravity.velocity * 0.7) }} title={`Velocity: ${gravity.velocity.toFixed(1)}`} />
                </div>
            </div>
        );
    }

	    return (
	        <div 
	            className={`
	                bg-gray-900 text-gray-200 border-gray-700 
	                ${compact ? 'border-t w-full rounded-none' : 'p-4 rounded-lg shadow-lg border w-64'}
	            `}
	            onMouseLeave={() => compact && !isCollapsed && setIsCollapsed(true)}
	        >
            <h3
                className={`
                    text-xs font-bold uppercase tracking-wider mb-2 flex justify-between items-center cursor-pointer select-none
                    ${compact ? 'px-4 pt-3 pb-1' : 'border-b border-gray-700 pb-2 mb-4'}
                `}
                onClick={() => compact && setIsCollapsed(!isCollapsed)}
            >
                <span className="flex items-center gap-2">
                    STRATEGY DIALS
                    {compact && <span className="text-[10px] text-gray-500">▼</span>}
                </span>
                {loading && <span className="text-[10px] text-yellow-500 animate-pulse">Saving...</span>}
	            </h3>
	
	            <div className={`space-y-3 ${compact ? 'px-3 pb-3' : ''}`}>
	                {dialControls}
	            </div>
	        </div>
	    );
};
